/* eslint-disable jsx-a11y/anchor-is-valid */

import EventNoteIcon from '@mui/icons-material/EventNote';
import Info from '@mui/icons-material/Info';
import LocalOffer from '@mui/icons-material/LocalOffer';
import LocationOn from '@mui/icons-material/LocationOn';
import { Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import React from 'react';
import { TwitterPicker } from 'react-color';
import { useSelector } from 'react-redux';
import { primary } from '../../../../../utils/brandColors';
import colors from '../../../../../utils/colors';
import BarnHorsesAutocomplete from '../../../../Autocompletes/BarnHorses';
import LocationsAutocomplete from '../../../../Autocompletes/Locations';
import TagsAutocomplete from '../../../../Autocompletes/Tags';
import HorseIcon from '../../../../CustomIcons/Horse';
import TagList from '../../../../TagList';

const HeaderSection = ({
  name,
  validation,
  setName,
  description,
  setDescription,
  metadata,
  setMetadata,
  location,
  barnHorses,
  handleChangeLocation,
  handleChangeBarnHorses,
  additionalTags,
  setAdditionalTags,
  detailedLocation,
  setDetailedLocation,
  selectedBarn,
  disabledForBeingInPast,
}) => {
  const [displayColorPicker, setDisplayColorPicker] = React.useState(false);
  const [addDescription, setAddDescription] = React.useState(false);
  const [addLocation, setAddLocation] = React.useState(false);
  const [changeTags, setChangeTags] = React.useState(false);
  const [changeHorses, setChangeHorses] = React.useState(false);

  const { barnHorses: boardBarnHorses } = useSelector((state) => state.board);

  React.useEffect(() => {
    setChangeTags(false);
    setChangeHorses(false);
    if (description && description.length > 0) {
      setAddDescription(true);
    } else {
      setAddDescription(false);
    }
    if (detailedLocation && detailedLocation.length > 0) {
      setAddLocation(true);
    } else {
      setAddLocation(false);
    }
  }, [name]);

  const listItemSx = {
    px: 1,
    py: 0.75,
  };
  const listItemIconSx = {
    minWidth: 32,
    mr: 0.75,
    '& .MuiSvgIcon-root': {
      fontSize: '1.25rem',
      width: '1.25rem',
      height: '1.25rem',
    },
  };

  return (
    <Stack spacing={2} sx={{ m: 1 }}>
      <Paper
        sx={{
          borderRadius: 3,
          m: 2,
        }}
      >
        <List disablePadding>
          <ListItem dense sx={listItemSx}>
            <ListItemIcon sx={listItemIconSx}><EventNoteIcon /></ListItemIcon>
            <ListItemText>
              <TextField
                sx={{ minWidth: '100%' }}
                size="small"
                variant="outlined"
                id="Name"
                label="Event Display Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={disabledForBeingInPast}
              />
            </ListItemText>
            {!disabledForBeingInPast && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
                onClick={() => { if (!displayColorPicker) { setDisplayColorPicker(true) } }}
                role="presentation"
              >
                <Tooltip title="Change event color">
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      border: '1px solid',
                      borderColor: (metadata && metadata.calendarDisplayColor) || primary.main,
                      backgroundColor: (metadata && metadata.calendarDisplayColor) || primary.main,
                      ml: 1,
                    }}
                  />
                </Tooltip>
                {displayColorPicker && (
                  <Box
                    sx={{
                      position: 'absolute',
                      right: 24,
                      top: 0,
                      zIndex: 100000,
                    }}
                  >
                    <Box
                      sx={{
                        position: 'fixed',
                        inset: 0,
                      }}
                      onClick={() => { setDisplayColorPicker(false); }}
                      role="presentation"
                    />
                    <Paper>
                      <TwitterPicker
                        colors={colors}
                        color={(metadata && metadata.calendarDisplayColor) || primary.main}
                        onChange={(e) => {
                          const calendarDisplayColor = e.hex;
                          setMetadata({
                            ...metadata,
                            calendarDisplayColor,
                          });
                          if (e.hex.length === 7) {
                            setDisplayColorPicker(false);
                          }
                        }}
                        direction="vertical"
                      />
                      <Box
                        sx={{
                          mx: 2,
                          pb: 1,
                          fontSize: 14,
                          fontWeight: 300,
                          zIndex: 10000,
                          position: 'relative',
                        }}
                      >
                        <Typography fontWeight={300} component="div">Pick your own color on the</Typography>
                        <a href="https://g.co/kgs/rxPsQ5R" target="blank" rel="noreferrer">Google Hex Color Picker</a>
                      </Box>
                    </Paper>
                  </Box>
                )}
              </Box>
            )}
          </ListItem>
          <ListItem dense sx={listItemSx}>
            <ListItemIcon sx={listItemIconSx}><Info /></ListItemIcon>
            <ListItemText>
              {addDescription
                ? (
                  <TextField
                    sx={{ minWidth: '100%' }}
                    size="small"
                    variant="outlined"
                    id="description"
                    label="Event Details"
                    value={description}
                    multiline
                    rows="3"
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={disabledForBeingInPast}
                  />
                )
                : (
                  <Link
                    component="button"
                    variant="body2"
                    color="secondary"
                    underline="hover"
                    onClick={() => setAddDescription(true)}
                  >
                    Add Event Details
                  </Link>
                )}
            </ListItemText>
          </ListItem>
          <ListItem dense sx={listItemSx}>
            <ListItemIcon sx={listItemIconSx}><LocalOffer /></ListItemIcon>
            <ListItemText>
              {changeTags
                ? (
                  <TagsAutocomplete
                    onSelect={(e, value) => { setAdditionalTags(value); }}
                    value={additionalTags}
                    size="small"
                    disabled={disabledForBeingInPast}
                  />
                )
                : (
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <TagList tags={additionalTags} disableGutters />
                    <Link
                      component="button"
                      variant="body2"
                      color="secondary"
                      underline="hover"
                      onClick={() => setChangeTags(true)}
                    >
                      Modify Tags
                    </Link>
                  </Stack>
                )}
            </ListItemText>
          </ListItem>
          <ListItem dense sx={listItemSx}>
            <ListItemIcon sx={listItemIconSx}><LocationOn /></ListItemIcon>
            <ListItemText>
              {addLocation
                ? (
                  <Stack spacing={1}>
                    <LocationsAutocomplete
                      multiple={false}
                      validation={validation.location}
                      value={location}
                      onSelect={(e, newLocations) => {
                        handleChangeLocation(newLocations)
                      }}
                      size="small"
                      limitTags={5}
                      disabled={disabledForBeingInPast}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      id="detailed-location"
                      label="Location Details"
                      value={detailedLocation || ''}
                      onChange={(e) => setDetailedLocation(e.target.value)}
                      disabled={disabledForBeingInPast}
                    />
                  </Stack>
                )
                : (
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2">
                      {(location && location.name) || 'No location selected'} {detailedLocation
                        ? `(${detailedLocation})`
                        : ''}
                    </Typography>
                    <Link
                      component="button"
                      variant="body2"
                      color="secondary"
                      underline="hover"
                      onClick={() => setAddLocation(true)}
                    >
                      Change Location
                    </Link>
                  </Stack>
                )}
            </ListItemText>
          </ListItem>
          <ListItem dense sx={listItemSx}>
            <ListItemIcon sx={listItemIconSx}><HorseIcon /></ListItemIcon>
            {changeHorses
              ? (
                <BarnHorsesAutocomplete
                  multiple
                  value={barnHorses}
                  onSelect={(e, newHorses) => {
                    handleChangeBarnHorses(newHorses)
                  }}
                  limitTags={5}
                  barnHorses={boardBarnHorses && boardBarnHorses.length > 0
                    ? boardBarnHorses
                    : undefined}
                  disabled={disabledForBeingInPast}
                />
              )
              : (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  {barnHorses.length > 0 && (
                    <TagList
                      tags={barnHorses.map((bh) => ({
                        id: parseInt(bh.pingumTagId, 10),
                        name: bh.horse.name,
                        tagType: { id: selectedBarn.barnMetadata.horseTagTypeId },
                      }))}
                    />
                  )}
                  <Link
                    component="button"
                    variant="body2"
                    color="secondary"
                    underline="hover"
                    onClick={() => setChangeHorses(true)}
                  >
                    Modify Horses
                  </Link>
                </Stack>
              )}
          </ListItem>
        </List>
      </Paper>
    </Stack>
  );
};

HeaderSection.propTypes = {
  name: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  validation: PropTypes.object.isRequired,
  setName: PropTypes.func.isRequired,
  setDescription: PropTypes.func.isRequired,
  metadata: PropTypes.any.isRequired,
  setMetadata: PropTypes.func.isRequired,
  location: PropTypes.object,
  barnHorses: PropTypes.any.isRequired,
  handleChangeLocation: PropTypes.func.isRequired,
  handleChangeBarnHorses: PropTypes.func.isRequired,
  additionalTags: PropTypes.array.isRequired,
  setAdditionalTags: PropTypes.func.isRequired,
  setDetailedLocation: PropTypes.func.isRequired,
  detailedLocation: PropTypes.string,
  selectedBarn: PropTypes.object.isRequired,
  disabledForBeingInPast: PropTypes.bool,
};

HeaderSection.defaultProps = {
  location: null,
  detailedLocation: null,
  disabledForBeingInPast: false,
};

export default HeaderSection;
